import type { MelodyLib } from '@tunasong/music-lib'
import type { CaptureDiagnosis } from '@tunasong/music-lib'
import type { Melody, MelodySource } from '@tunasong/schemas'
import { Button, Checkbox, Dialog, FormControlLabel, useUserConfig } from '@tunasong/ui-lib'
import { useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import { useMelodyRollPlayback } from '../melody-roll/use-melody-roll-playback.js'
import { PitchInputMeter } from '../pitch-training/pitch-input-meter.js'
import { useCaptureMonitor } from '../pitch-training/use-capture-monitor.js'
import { usePlayNotes } from '../player/piano-player.hook.js'
import { CaptureReview } from './capture-review.js'
import { CaptureSettings, type CaptureSettingsValue } from './capture-settings.js'
import { AUDIO_LEAD_IN_MS, DEFAULT_BARS, runMelodyTake, TAKE_TAIL_MS, type TakePhase } from './capture-take.js'
import { buildClickTrack, CLICK_INSTRUMENT, countInDurationMs, takeDurationMs } from './click-track.js'
import { hasHeadphoneConsent, withHeadphoneConsent } from './headphone-consent.js'
import styles from './melody-capture.module.css'
import { framesToRawNotes, useAudioCapture } from './use-audio-capture.js'
import { useCaptureSave } from './use-capture-save.js'
import { useMidiCapture } from './use-midi-capture.js'

/**
 * Capture a melody from a performance: pick how you are playing it, count in, play to the click,
 * review what came back.
 *
 * Two rules this component exists to enforce, both of them about what happens BEFORE a microphone
 * opens:
 *
 *  1. **A take is always started by a click, never by an effect.** A browser will not resume an
 *     `AudioContext` without a user gesture, and an effect on an unstable dependency would re-open
 *     the microphone mid-take. Same rule the Pitch Trainer states for the same reason.
 *  2. **Audio capture does not start until headphones are confirmed.** The click sounds for the
 *     whole take and the tracker segments everything it hears: through a speaker the metronome is
 *     captured on every beat and written into the melody as a note on every beat (§5).
 *
 * The take's tempo, meter and grid become the melody's. They have to: `onset` is in beats and
 * `offsetMs` is measured against a grid line, so notes captured at 96 bpm mean nothing under a
 * melody that claims 120.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

export interface MelodyCaptureProps {
  melody: Melody
  open: boolean
  /**
   * Where a take's recording is filed. The melody entity for a library melody, the host document
   * for an embedded one - a recording belongs somewhere the user can find it again.
   */
  parentId?: string
  onCapture: (melody: Melody) => void
  onClose: () => void
}

interface ReviewState {
  raw: MelodyLib.RawNote[]
  source: MelodySource
  diagnosis: CaptureDiagnosis | null
  melody: Melody
}

const PHASE_TEXT: Record<TakePhase, string> = {
  'lead-in': 'Listening to the room — stay quiet for a moment.',
  'count-in': 'Two knocks, then play.',
  take: 'Recording.',
  quantizing: 'Working out what you played...',
}

export const MelodyCapture: FC<MelodyCaptureProps> = props => {
  const { melody, open, parentId, onCapture, onClose } = props

  const [userConfig, updateConfig] = useUserConfig()
  const confirmedDevices = userConfig?.musicPreferences?.headphoneConfirmedDevices

  const audio = useAudioCapture()
  const midi = useMidiCapture()
  const monitor = useCaptureMonitor()
  const { saveRecording } = useCaptureSave()
  const { playNotes: playClickNotes } = usePlayNotes(CLICK_INSTRUMENT)
  const playback = useMelodyRollPlayback({ melody })

  const [settings, setSettings] = useState<CaptureSettingsValue>(() => ({
    modality: melody.instrumentHint === 'voice' ? 'voice' : 'midi',
    bpm: melody.tempo.bpm,
    meter: melody.tempo.meter,
    grid: melody.grid,
    bars: DEFAULT_BARS,
  }))
  const [confirmedThisSession, setConfirmedThisSession] = useState(false)
  const [phase, setPhase] = useState<TakePhase | null>(null)
  const [review, setReview] = useState<ReviewState | null>(null)

  /** Every scheduled click, so Stop and unmount can cancel the ones that have not sounded. */
  const timeouts = useRef<number[]>([])
  /** Resolves the take's current wait early - this is the whole of the Stop button. */
  const stopWait = useRef<(() => void) | null>(null)
  /** When the click track started, on `performance.now()`, which is the MIDI events' clock. */
  const clickStartedAt = useRef(0)

  const clearTimeouts = () => {
    for (const handle of timeouts.current) {
      window.clearTimeout(handle)
    }
    timeouts.current = []
  }

  useEffect(
    () => () => {
      clearTimeouts()
      stopWait.current = null
    },
    []
  )

  const isAudioModality = settings.modality !== 'midi'
  const consented = confirmedThisSession || hasHeadphoneConsent(confirmedDevices, audio.deviceLabel)
  const canStart = !isAudioModality || consented

  const handleConfirmHeadphones = (checked: boolean) => {
    setConfirmedThisSession(checked)
    if (!checked) {
      return
    }
    /*
     * Only a device the browser has named can be remembered, and it has no name until a microphone
     * has been opened once - so the first capture on a machine always asks, which is the right way
     * round for a claim about what the user is physically wearing.
     */
    const next = withHeadphoneConsent(confirmedDevices, audio.deviceLabel)
    if (next !== confirmedDevices) {
      updateConfig('musicPreferences', { ...userConfig?.musicPreferences, headphoneConfirmedDevices: next })
    }
  }

  const wait = (ms: number) =>
    new Promise<void>(resolve => {
      const handle = window.setTimeout(() => {
        stopWait.current = null
        resolve()
      }, ms)
      timeouts.current.push(handle)
      stopWait.current = () => {
        window.clearTimeout(handle)
        stopWait.current = null
        resolve()
      }
    })

  const handleStop = () => {
    clearTimeouts()
    stopWait.current?.()
  }

  const handleStart = async () => {
    const countInMs = countInDurationMs(settings.bpm)
    const takeMs = takeDurationMs(settings)
    const clickTrack = buildClickTrack(settings)
    const captured: Melody = { ...melody, tempo: { bpm: settings.bpm, meter: settings.meter }, grid: settings.grid }

    monitor.reset()
    if (!isAudioModality) {
      midi.arm()
    }

    const result = await runMelodyTake(
      {
        stopPlayback: playback.stop,
        playClick: () => {
          clickStartedAt.current = performance.now()
          for (const stab of clickTrack) {
            timeouts.current.push(window.setTimeout(() => playClickNotes(stab.events), stab.delayMs))
          }
        },
        wait,
        onPhase: setPhase,
        startFrames: isAudioModality ? audio.startFrames : undefined,
        startRecording: isAudioModality ? audio.startRecording : undefined,
        onFrame: isAudioModality ? monitor.onFrame : undefined,
      },
      {
        leadInMs: isAudioModality ? AUDIO_LEAD_IN_MS : 0,
        countInMs,
        takeMs,
        tailMs: TAKE_TAIL_MS,
      }
    )
    clearTimeouts()

    if (!isAudioModality) {
      const t0 = clickStartedAt.current + countInMs
      setReview({
        raw: midi.collect(t0, t0 + takeMs + TAKE_TAIL_MS),
        source: { kind: 'midi' },
        diagnosis: null,
        melody: captured,
      })
      setPhase(null)
      return
    }

    const { raw, diagnosis } = framesToRawNotes(result.frames, result.t0Ms)
    /*
     * The recording is saved even when nothing was heard: it is the evidence for the diagnosis, and
     * §2 says the source recording is never discarded.
     */
    const entityId = result.blob ? await saveRecording(result.blob, `${melody.name ?? 'Melody'} take`, parentId) : null
    setReview({
      raw,
      source: entityId ? { kind: 'audio', entityId } : { kind: 'audio' },
      diagnosis,
      melody: captured,
    })
    setPhase(null)
  }

  const handleAccept = (next: Melody) => {
    onCapture(next)
    setReview(null)
    onClose()
  }

  const handleRetake = () => {
    setReview(null)
    setPhase(null)
  }

  const handleClose = () => {
    handleStop()
    setReview(null)
    setPhase(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} title="Capture melody" fullWidth={true} maxWidth="md">
      <div className={styles.root}>
        {review ? (
          <CaptureReview
            melody={review.melody}
            raw={review.raw}
            source={review.source}
            diagnosis={review.diagnosis}
            onAccept={handleAccept}
            onRetake={handleRetake}
            onCancel={handleClose}
          />
        ) : phase ? (
          <div className={styles.taking}>
            <span className={styles.phase} role="status">
              {PHASE_TEXT[phase]}
            </span>
            {isAudioModality ? (
              <PitchInputMeter
                frame={monitor.frame}
                noiseFloorRms={monitor.noiseFloorRms}
                deviceLabel={audio.deviceLabel}
              />
            ) : null}
            <div className={styles.actions}>
              <Button onClick={handleStop}>Stop</Button>
            </div>
          </div>
        ) : (
          <>
            <CaptureSettings value={settings} onChange={setSettings} midiAvailable={midi.hasInput} />

            {isAudioModality ? (
              <div className={styles.headphones}>
                <FormControlLabel
                  label="I am wearing headphones"
                  control={
                    <Checkbox
                      checked={consented}
                      disabled={hasHeadphoneConsent(confirmedDevices, audio.deviceLabel)}
                      onCheckedChange={handleConfirmHeadphones}
                    />
                  }
                />
                <span className={styles.headphonesWhy}>
                  The click plays for the whole take. Out of a speaker it would be recorded too, and every beat
                  of it would end up in the melody as a note.
                </span>
              </div>
            ) : null}

            <div className={styles.actions}>
              <Button disabled={!canStart} onClick={handleStart}>
                Start take
              </Button>
              <Button onClick={handleClose}>Cancel</Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}
